import React from 'react';
import { Box, CardContent, Chip, Container, Typography, useTheme, alpha } from '@mui/material';
import { CLIENT_CATEGORIES } from '../../data/siteContent';
import { SECTION_SCROLL_MARGIN } from '../../utils/scroll';
import { MotionCard, fadeInUp, useMotionProps, useReducedMotion } from '../motion';
import SectionHeading from './SectionHeading';

const Clients = () => {
  const theme = useTheme();
  const motionProps = useMotionProps();
  const reducedMotion = useReducedMotion();

  return (
    <Box
      id="clients"
      component="section"
      aria-labelledby="clients-heading"
      sx={{ scrollMarginTop: SECTION_SCROLL_MARGIN, py: { xs: 8, md: 12 }, backgroundColor: 'background.paper' }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          id="clients-heading"
          eyebrow="Trusted Partners"
          title="Our Clients"
          subtitle="Trusted by cooperatives and enterprises across Sri Lanka."
        />

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2.5, sm: 3 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
            alignItems: 'stretch'
          }}
        >
          {CLIENT_CATEGORIES.map((category, index) => (
            <MotionCard
              key={category.title}
              {...motionProps(fadeInUp(index * 0.08))}
              whileHover={reducedMotion ? undefined : { y: -6 }}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  boxShadow: `0 18px 48px ${alpha(theme.palette.primary.main, 0.14)}`
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                <Typography
                  variant="h4"
                  component="p"
                  sx={{
                    fontWeight: 900,
                    mb: 0.5,
                    fontVariantNumeric: 'tabular-nums',
                    background: `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent'
                  }}
                >
                  {category.count}
                </Typography>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 800, mb: 1.5 }}>
                  {category.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: category.clients.length ? 2.5 : 0, lineHeight: 1.7 }}>
                  {category.description}
                </Typography>

                {category.clients.length > 0 && (
                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 800, mb: 1.5 }}>
                      Featured Clients
                    </Typography>
                    {/* Scrollable and keyboard focusable, so the list is reachable without a mouse. */}
                    <Box
                      tabIndex={0}
                      role="group"
                      aria-label={`${category.title} client list`}
                      sx={{
                        maxHeight: 220,
                        overflowY: 'auto',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        p: 0.5,
                        borderRadius: 2,
                        // Fade the lower edge to hint that the list scrolls.
                        maskImage: 'linear-gradient(180deg, #000 85%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(180deg, #000 85%, transparent 100%)',
                        '&::-webkit-scrollbar': { width: 6 },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.25),
                          borderRadius: 3
                        }
                      }}
                    >
                      {category.clients.map((client) => (
                        <Chip
                          key={client}
                          label={client}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: alpha(theme.palette.primary.main, 0.2),
                            backgroundColor: alpha(theme.palette.primary.main, 0.03)
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Clients;
